import React, { MouseEvent, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Tooltip } from '@mui/material'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { Button } from 'src/components/shared/Button'
import type { MenuActionItem } from 'src/interfaces/ui'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  items: MenuActionItem[]
  /** Accessible name of the trigger, e.g. "Market actions". */
  ariaLabel: string
  dataTestId?: string
}

/**
 * The row-actions pattern from apps/dashboard's tables (`TeamMemberActionButton` and friends):
 * one white "⋯" button at the row's right edge that opens an `ActionMenu`, instead of a row of
 * icon buttons. Clicks stop at the cell so a row `onClick` (open details) does not fire, and
 * picking an item closes the menu before the action runs.
 */
const RowActionsMenuComponent = ({ items, ariaLabel, dataTestId }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const closingItems = useMemo<MenuActionItem[]>(() => {
    return items.map((item) => {
      return {
        ...item,
        onClick: () => {
          setAnchorEl(null)
          item.onClick()
        },
      }
    })
  }, [items])

  if (items.length === 0) {
    // Nothing can be done to this row — a menu that opens onto an empty list is worse than no
    // menu, so the cell stays blank.
    return null
  }

  return (
    <div css={tw`flex h-full w-full flex-row items-center justify-end`}>
      <Tooltip title="Actions">
        <span>
          {/* Same box as the dashboard's row trigger: tiny (28px) white with the default 8px side
              padding around the 20px glyph — 36×28, not the 28px square `iconOnly` would give. */}
          <Button
            size="tiny"
            color="white"
            aria-label={ariaLabel}
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl)}
            leftIcon={
              <GSIcon iconName="gs_dot_grid_1x3_horizontal" size={20} css={tw`text-shades-1000`} />
            }
            onClick={handleOpen}
            data-testid={dataTestId}
          />
        </span>
      </Tooltip>
      <ActionMenu
        items={closingItems}
        isOpen={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleClose}
      />
    </div>
  )
}

export const RowActionsMenu = memo(RowActionsMenuComponent)
